import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button, TextField } from "@pack-it-up/ui-web";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { AuthCard, AuthError } from "./AuthCard.js";
import { ProviderButtons } from "./ProviderButtons.js";
import { signUpSchema, isValid } from "./schemas.js";

export const SignUp = (): ReactNode => {
  const { auth } = useStores();
  const error = useStoreValue(auth, (s) => s.error);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const valid = isValid(signUpSchema, { displayName, email, password });

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    // The store captures the device timezone + default reminder hour for the profile
    // write (Doc 04 §6, Doc 09 §1) on success.
    // No imperative navigate on success: the router gate flips off the auth stream
    // (which fires mid-signUp, before the profile writes resolve). By the time this
    // promise settles the page is unmounted — a stale navigate("/") here can fire
    // AFTER the user has already moved elsewhere in the signed-in app and yank them
    // back to home (seen as a CI race: sign up → create trip → bounced off it).
    await auth.getState().signUp(email, password, displayName);
    setSubmitting(false);
  };

  return (
    <AuthCard
      title="Create your account"
      footer={
        <span>
          Already have an account? <Link to="/signin">Sign in</Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }} noValidate>
        <TextField label="Name" autoComplete="name" value={displayName} onChange={setDisplayName} />
        <TextField label="Email" type="email" autoComplete="email" value={email} onChange={setEmail} />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={setPassword}
        />
        {error !== null && <AuthError message={error} />}
        <Button type="submit" disabled={!valid || submitting}>
          {submitting ? "Creating…" : "Create account"}
        </Button>
        <ProviderButtons />
      </form>
    </AuthCard>
  );
};
